import { Routes } from '@angular/router';
import { businessProfileGuard, adminGuard, businessGuard, adminOnlyGuard, managerOnlyGuard, businessCabinetGuard, businessContextGuard, accountingGuard, internalUserGuard } from './core/guards/auth.guard';
import { pendingChangesGuard } from './core/guards/pending-changes.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { cabinetPermissionGuard } from './core/guards/cabinet-permission.guard';
import { scanShortcutGuard } from './core/guards/scan-shortcut.guard';
import { CABINET_PERMISSION, PERMISSION } from './core/utils/auth-permissions';
import { cabinetSectionPermissions } from './core/utils/cabinet-sections';
import { userCardResolver } from './features/admin/user-card/user-card.resolver';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./features/auth/auth-entry-redirect.component').then(m => m.AuthEntryRedirectComponent),
  },
  {
    path: 'admin',
    redirectTo: 'admin/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    loadComponent: () => import('./features/auth/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'auth/login',
    redirectTo: '/auth',
    pathMatch: 'full'
  },
  {
    path: 'auth/register',
    loadComponent: () => import('./features/auth/register.component').then(m => m.RegisterComponent)
  },
  {
    path: 'auth/forgot-password',
    loadComponent: () => import('./features/auth/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    path: 'auth/reset-password',
    loadComponent: () => import('./features/auth/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/auth/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: 'auth/password/reset',
    loadComponent: () => import('./features/auth/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: 'register',
    loadComponent: () => import('./features/auth/register.component').then(m => m.RegisterComponent)
  },
  // BE-1258: регистрация бизнеса живёт на `/join`, код едет сегментом пути по образцу
  // агентской `/auth/{code}` из BE-1120. Это сам экран регистрации, а не редирект: код
  // попадает в форму прямо из маршрута и работает независимо от хранилища. `/join` —
  // адрес, до которого экран подчищает себя после захвата, он же переживает F5.
  {
    path: 'join',
    loadComponent: () => import('./features/client-registration/client-registration.component').then(m => m.ClientRegistrationComponent)
  },
  {
    path: 'join/:code',
    loadComponent: () => import('./features/client-registration/client-registration.component').then(m => m.ClientRegistrationComponent)
  },
  // Старый адрес регистрации. Удалить нельзя — по нему разосланы ссылки с `?invite_code=`
  // и `?ref=`; редирект их не теряет, Angular переносит query на цель. Экран после
  // захвата подчищает адрес до `/join`, так что старый путь нигде не остаётся видимым.
  {
    path: 'client-registration',
    redirectTo: 'join',
    pathMatch: 'full'
  },
  {
    path: 'registration',
    canActivate: [businessGuard],
    loadComponent: () => import('./features/registration/registration.component').then(m => m.RegistrationComponent)
  },
  {
    path: 'registration-after-init',
    loadComponent: () => import('./features/registration-after-init/registration-after-init.component').then(m => m.RegistrationAfterInitComponent)
  },
  {
    path: 'dashboard',
    redirectTo: '/businesses',
    pathMatch: 'full'
  },
  {
    path: 'accounting',
    redirectTo: 'accounting/invoices',
    pathMatch: 'full'
  },
  {
    path: 'accounting/invoices',
    canActivate: [accountingGuard, permissionGuard(PERMISSION.accountingInvoicesView)],
    loadComponent: () => import('./features/accounting/accounting-invoices/accounting-invoices.component').then(m => m.AccountingInvoicesComponent)
  },
  {
    path: 'admin/dashboard',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent)
  },
  {
    // BE-1075: дашборды аналитики — только Админ/root (ручки /admin/analytics/* отвечают менеджеру 403).
    path: 'admin/statistics',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-statistics/admin-statistics.component').then(m => m.AdminStatisticsComponent)
  },
  {
    path: 'admin/businesses',
    canActivate: [adminGuard, permissionGuard(PERMISSION.businessesView)],
    loadComponent: () => import('./features/admin/business-list/business-list.component').then(m => m.BusinessListComponent)
  },
  {
    path: 'admin/businesses/create',
    canActivate: [adminGuard, permissionGuard(PERMISSION.businessesCreate)],
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/admin/business-create/business-create.component').then(m => m.BusinessCreateComponent)
  },
  {
    path: 'admin/business-list',
    redirectTo: 'admin/businesses',
    pathMatch: 'full'
  },
  {
    path: 'admin/businesses/:business_id',
    canActivate: [adminGuard, permissionGuard(PERMISSION.businessesView)],
    // BE-1556: уход с изменённой формой правки акции (вкладка «Акции») — с вопросом.
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/admin/business-profile/business-profile.component').then(m => m.BusinessProfileComponent)
  },
  {
    path: 'admin/businesses/:business_id/edit',
    canActivate: [adminGuard, permissionGuard(PERMISSION.businessesEdit)],
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/admin/business-edit/business-edit.component').then(m => m.BusinessEditComponent)
  },
  {
    path: 'admin/promotions',
    canActivate: [adminGuard, permissionGuard(PERMISSION.promotionsView)],
    // BE-1556: «Назад» и свайп назад с открытой изменённой формой акции — с вопросом.
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/admin/business-promotions/business-promotions.component').then(m => m.BusinessPromotionsComponent)
  },
  {
    // BE-763: раздел «Пользователи» с табами (Все/Игроки/Бизнес-аккаунты/Команда)
    path: 'admin/users',
    canActivate: [adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-users/admin-users.component').then(m => m.AdminUsersComponent)
  },
  {
    // BE-767: богатая карточка пользователя; в этой задаче реализована ветка `player`
    path: 'admin/users/:type/:id',
    canActivate: [adminOnlyGuard],
    resolve: { card: userCardResolver },
    loadComponent: () => import('./features/admin/user-card/user-card.component').then(m => m.UserCardComponent)
  },
  {
    // BE-763: «Менеджеры» поглощены табом «Команда»; пункт меню убран, маршрут оставлен доступным
    path: 'admin/managers',
    canActivate: [adminOnlyGuard],
    loadComponent: () => import('./features/admin/user-list/user-list.component').then(m => m.UserListComponent)
  },
  {
    path: 'admin/user-list',
    redirectTo: 'admin/managers',
    pathMatch: 'full'
  },
  {
    path: 'admin/initial-registrations',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/initial-registration-list/initial-registration-list.component').then(m => m.InitialRegistrationListComponent)
  },
  {
    // Раздел админский целиком: у менеджера скрыт в меню и закрыт по прямой ссылке.
    path: 'admin/referral',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-referral/admin-referral.component').then(m => m.AdminReferralComponent)
  },
  {
    path: 'admin/kubishkas',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-kubishka/admin-kubishka.component').then(m => m.AdminKubishkaComponent)
  },
  {
    path: 'admin/kubishkas/create',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/kubishka-form/kubishka-form.component').then(m => m.KubishkaFormComponent)
  },
  {
    path: 'admin/kubishkas/:kubishkaId/edit',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/kubishka-form/kubishka-form.component').then(m => m.KubishkaFormComponent)
  },
  {
    path: 'admin/kubishkas/:kubishkaId',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/kubishka-detail/kubishka-detail.component').then(m => m.KubishkaDetailComponent)
  },
  {
    // BE-1303: рассылки игрокам. Роль проверяется маршрутом, а не только меню: скрытый
    // пункт от прямого захода не защищает.
    path: 'admin/broadcasts',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-broadcasts/admin-broadcasts.component').then(m => m.AdminBroadcastsComponent)
  },
  {
    // BE-1520: каталог автосообщений. Только Админ — роль проверяет маршрут, как у «Рассылок»;
    // уход с несохранёнными правками карточки или общих настроек — с вопросом.
    path: 'admin/auto-messages',
    canActivate: [adminGuard, adminOnlyGuard],
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/admin/admin-auto-messages/admin-auto-messages.component').then(m => m.AdminAutoMessagesComponent)
  },
  {
    // Закрытие игрового сезона. Роль проверяется маршрутом: операция необратима,
    // и одного скрытого пункта меню тут мало.
    path: 'admin/season',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-season/admin-season.component').then(m => m.AdminSeasonComponent)
  },
  {
    // BE-824: личная страница сотрудника платформы. adminGuard тут не годится — он
    // уводит бухгалтера на счета раньше прочих проверок, и до своего профиля тот бы не дошёл.
    path: 'admin/profile',
    canActivate: [internalUserGuard],
    loadComponent: () => import('./features/admin/my-profile/my-profile.component').then(m => m.MyProfileComponent)
  },
  {
    // BE-1252: личный кошелёк менеджера. Роль проверяется маршрутом, а не только меню.
    path: 'admin/my-reward',
    canActivate: [adminGuard, managerOnlyGuard],
    loadComponent: () => import('./features/admin/manager-reward/my-reward.component').then(m => m.MyRewardComponent)
  },
  {
    path: 'admin/audit-logs',
    canActivate: [adminGuard, permissionGuard(PERMISSION.auditView)],
    loadComponent: () => import('./features/admin/admin-audit-logs/admin-audit-logs.component').then(m => m.AdminAuditLogsComponent)
  },
  {
    path: 'admin/promocodes',
    canActivate: [adminGuard, permissionGuard(PERMISSION.promocodesView)],
    loadComponent: () => import('./features/admin/admin-promocodes/admin-promocodes.component').then(m => m.AdminPromocodesComponent)
  },
  {
    path: 'admin/tasks',
    canActivate: [adminGuard, adminOnlyGuard],
    loadComponent: () => import('./features/admin/admin-tasks/admin-tasks.component').then(m => m.AdminTasksComponent)
  },
  {
    // BE-610: отчёты бизнесов
    path: 'admin/reports',
    canActivate: [adminGuard, permissionGuard(PERMISSION.reportsView)],
    loadComponent: () => import('./features/admin/admin-reports/admin-reports.component').then(m => m.AdminReportsComponent)
  },
  {
    // BE-372: панель тикетов менеджеру
    path: 'admin/manager-tickets',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/admin-manager-tickets/admin-manager-tickets.component').then(m => m.AdminManagerTicketsComponent)
  },
  {
    path: 'admin/categories',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/admin-categories/admin-categories.component').then(m => m.AdminCategoriesComponent)
  },
  {
    // BE-880: список магазинов бизнеса (админ/менеджер)
    path: 'admin/shops',
    canActivate: [adminGuard, permissionGuard(PERMISSION.shopsView)],
    loadComponent: () => import('./features/admin/admin-shops/admin-shops.component').then(m => m.AdminShopsComponent)
  },
  {
    // BE-857: модерация карточки конкретного магазина (per-shop)
    path: 'admin/shops/:shop_id/moderate',
    canActivate: [adminGuard, permissionGuard(PERMISSION.shopsStatusManage)],
    loadComponent: () => import('./features/admin/admin-shop-moderation/admin-shop-moderation.component').then(m => m.AdminShopModerationComponent)
  },
  {
    // BE-880: карточка магазина (админ/менеджер)
    path: 'admin/shops/:shop_id',
    canActivate: [adminGuard, permissionGuard(PERMISSION.shopsView)],
    loadComponent: () => import('./features/admin/admin-shop-detail/admin-shop-detail.component').then(m => m.AdminShopDetailComponent)
  },
  {
    // BE-787: заявки на акции (оферты) — раздел «Модерация»
    path: 'admin/applications',
    canActivate: [adminGuard, permissionGuard(PERMISSION.applicationsView)],
    loadComponent: () => import('./features/admin/admin-applications/admin-applications.component').then(m => m.AdminApplicationsComponent)
  },
  {
    // BE-433 + BE-787: документы — платформенные PDF + публичный каталог
    path: 'admin/documents',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/admin-documents/admin-documents.component').then(m => m.AdminDocumentsComponent)
  },
  {
    // BE-664: отдельный экран уведомлений (вместо выпадающей панели)
    path: 'admin/notifications',
    canActivate: [adminGuard],
    loadComponent: () => import('./features/admin/admin-notifications/admin-notifications.component').then(m => m.AdminNotificationsComponent)
  },
  {
    path: 'promotions',
    canActivate: [businessProfileGuard],
    loadComponent: () => import('./features/promotions/promotions.component').then(m => m.PromotionsComponent)
  },
  {
    path: 'promotions/create',
    canActivate: [businessProfileGuard],
    canDeactivate: [pendingChangesGuard],
    loadComponent: () => import('./features/promotions/promotion-create.component').then(m => m.PromotionCreateComponent)
  },
  {
    path: 'promotions/:id',
    canActivate: [businessProfileGuard],
    loadComponent: () => import('./features/promotions/promotion-detail.component').then(m => m.PromotionDetailComponent)
  },
  // Бизнес-кабинет (owner / employee)
  {
    path: 'businesses',
    canActivate: [businessCabinetGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./features/business-cabinet/business-list/business-list-cabinet.component').then(m => m.BusinessListCabinetComponent)
      },
      {
        path: ':business_id',
        canActivate: [businessContextGuard],
        loadComponent: () => import('./features/business-cabinet/cabinet-layout/cabinet-layout.component').then(m => m.CabinetLayoutComponent),
        children: [
          { path: '', loadComponent: () => import('./features/business-cabinet/cabinet-dashboard/cabinet-dashboard.component').then(m => m.CabinetDashboardComponent) },
          { path: 'profile/edit', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.profileEdit)], loadComponent: () => import('./features/business-cabinet/cabinet-profile-edit/cabinet-profile-edit.component').then(m => m.CabinetProfileEditComponent) },
          { path: 'profile', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.profileView)], loadComponent: () => import('./features/business-cabinet/cabinet-profile/cabinet-profile.component').then(m => m.CabinetProfileComponent) },
          // BE-1346: партнёрские ссылки связанного игрового профиля владельца
          { path: 'partner-links', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.profileView)], loadComponent: () => import('./features/business-cabinet/cabinet-partner-links/cabinet-partner-links.component').then(m => m.CabinetPartnerLinksComponent) },
          { path: 'marketplace', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.shopsView)], loadComponent: () => import('./features/business-cabinet/cabinet-marketplace/cabinet-marketplace.component').then(m => m.CabinetMarketplaceComponent) },
          { path: 'promotions', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.promotionsView)], loadComponent: () => import('./features/business-cabinet/cabinet-promotions/cabinet-promotions.component').then(m => m.CabinetPromotionsComponent) },
          { path: 'promotions/create', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.promotionsEdit)], canDeactivate: [pendingChangesGuard], loadComponent: () => import('./features/promotions/promotion-create.component').then(m => m.PromotionCreateComponent) },
          { path: 'promotions/:id/edit', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.promotionsEdit)], canDeactivate: [pendingChangesGuard], loadComponent: () => import('./features/promotions/promotion-edit.component').then(m => m.PromotionEditComponent) },
          { path: 'promotions/:id', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.promotionsView)], loadComponent: () => import('./features/promotions/promotion-detail.component').then(m => m.PromotionDetailComponent) },
          { path: 'reports', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.reportsView)], loadComponent: () => import('./features/business-cabinet/cabinet-reports/cabinet-reports.component').then(m => m.CabinetReportsComponent) },
          // API-токены бизнеса для внешних интеграций
          { path: 'integrations', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.integrationsView)], canDeactivate: [pendingChangesGuard], loadComponent: () => import('./features/business-cabinet/cabinet-integrations/cabinet-integrations.component').then(m => m.CabinetIntegrationsComponent) },
          { path: 'users', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.staffView)], loadComponent: () => import('./features/business-cabinet/cabinet-users/cabinet-users.component').then(m => m.CabinetUsersComponent) },
          // BE-375: сканирование промокода
          { path: 'scan', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.scannerScan)], loadComponent: () => import('./features/business-cabinet/cabinet-scan/cabinet-scan.component').then(m => m.CabinetScanComponent) },
          // BE-381: история сканирований промокодов
          { path: 'scan-history', canActivate: [cabinetPermissionGuard(cabinetSectionPermissions('scan-history'))], loadComponent: () => import('./features/business-cabinet/cabinet-scan-history/cabinet-scan-history.component').then(m => m.CabinetScanHistoryComponent) },
          // BE-371: список заявок бизнеса
          { path: 'tickets', loadComponent: () => import('./features/business-cabinet/cabinet-tickets/cabinet-tickets.component').then(m => m.CabinetTicketsComponent) },
          // п. 6.1: документы — постоянный доступ из футера сайдбара
          { path: 'documents', loadComponent: () => import('./features/business-cabinet/cabinet-documents/cabinet-documents.component').then(m => m.CabinetDocumentsComponent) },
          { path: 'accounting', canActivate: [cabinetPermissionGuard(CABINET_PERMISSION.invoicesView)], loadComponent: () => import('./features/business-cabinet/cabinet-accounting/cabinet-accounting.component').then(m => m.CabinetAccountingComponent) },
          // BE-664: отдельный экран уведомлений (вместо выпадающей панели)
          { path: 'notifications', loadComponent: () => import('./features/business-cabinet/cabinet-notifications/cabinet-notifications.component').then(m => m.CabinetNotificationsComponent) }
        ]
      }
    ]
  },
  // BE-1484: короткий адрес сканера — для закладки и ярлыка «Сканер» установленного
  // кабинета. Ничего не показывает: гвард сам уводит в сканер нужного бизнеса.
  {
    path: 'scan',
    canActivate: [scanShortcutGuard],
    children: []
  },
  {
    path: '**',
    redirectTo: '/auth'
  }
];
